/* The top bar. Three separate lists, because they answer different questions —
 * all three of them versions of "who is in front of me and what do I do with
 * them", which is why they are side by side rather than three places to look.
 *
 * "Next availability" leads: it is the walk-in path — pick a type and the board
 * finds the tech. Each row carries its own hotkey and, optionally, who is up
 * next for that service, so the choice of service and the choice of technician
 * are read in one place — which is what the list is named after. It is the list
 * that is always there, so it holds the left edge and the board's gutter
 * alignment; the other one comes and goes through the day and must not move it
 * when it does. Five services to a column, two columns, and the rest behind a
 * More.
 *
 * "Due now" beside it is whoever is about to walk in: every booking starting
 * inside the next few minutes, newest deadline first. Clicking one opens the
 * assign sheet already filled in — the type from the service mapping, the tech
 * from the book — so a client at the desk is two clicks from a square. The
 * chips themselves live in due-now.js, because a phone reads the same list out
 * of a collapsed strip and one question deserves one answer.
 *
 * It is a list of thin rows rather than a grid of cards. The bar sits above the
 * board and every millimetre it takes is a millimetre of squares pushed off the
 * bottom of the screen, so the same words are laid out along one line each and
 * aligned into columns: service, then who, then when. Ten of those read down
 * faster than ten small cards read across, and cost about half the height.
 *
 * The second list only exists while a scheduling plugin is on; with no book
 * there is nothing due.
 *
 * "Waiting" third is whoever checked themselves in on the lobby tablet and
 * could not be seated — see waiting-list.js, and js/components/check-in.js for
 * the other end of it. Third and last on purpose: it comes and goes through the
 * day like Due now does, and putting it anywhere earlier would move a list that
 * is already there every time somebody walked through the door. As it is,
 * nothing that existed before it moves when it appears.
 *
 * With no lobby tablet out, nobody ever checks in and the list is never there,
 * so this costs a salon that does not use it exactly nothing on screen. */
import * as Vue from 'vue';
import { store } from '../store.js';
import { DueNow } from './due-now.js';
import { PendingMoves } from './pending-moves.js';
import { WaitingList } from './waiting-list.js';

export const ServiceBar = {
  name: 'ServiceBar',
  components: { DueNow: DueNow, PendingMoves: PendingMoves, WaitingList: WaitingList },
  setup: function () {
    var suggestedName = Vue.computed(function () {
      var id = store.suggestedStaffId.value;
      if (!id) return null;
      var s = store.staffOf(id);
      return s ? s.name : null;
    });

    /* Who each service would go to, worked out for the whole menu at once.
     *
     * Three separate lookups per button used to ask the same question — once
     * for the grey, once for the italics, once for the name — and each of them
     * walked the rotation. One pass, and the buttons read the same answer.
     *
     * It carries the wait as well as the name now: nobody free is not the same
     * fact as nobody who does it, and only one of the two is a dead end. See
     * `nextUpDetail` for how the estimate is arrived at.
     *
     * With a party size over one every row answers for that many people
     * side by side instead — `partyDetail`, in the same shape, so the row
     * itself does not need to know which question it is answering. */
    var nextUp = Vue.computed(function () {
      var out = {};
      var n = store.ui.partySize;
      store.quickAddServices.value.forEach(function (svc) {
        out[svc.id] = n > 1 ? store.partyDetail(svc.id, n) : store.nextUpDetail(svc.id);
      });
      return out;
    });

    /* How many columns the menu comes to, handed to the CSS as a number.
     *
     * The list flows into its own columns — five services to each, see
     * `.service-chips` — so the grid works the count out for itself and does
     * not need telling. What CSS cannot work out is how wide the whole list is
     * entitled to be, because that is the count times what one row is worth,
     * and a grid has no way to read its own track count back. Without it a
     * wall screen gives two columns half a metre each, the rows stop at their
     * own max-width, and the gap down the middle of the menu is wider than the
     * rows either side of it. */
    var chipCols = Vue.computed(function () {
      return Math.max(1, Math.ceil(shownServices.value.length / 5));
    });

    /* The first ten, and the rest behind a More. Ten is two columns of five:
     * past that the menu starts pushing the board off the screen for services
     * the desk reaches for a few times a week. Hidden is not gone — a service's
     * key still starts it, and the hover card still answers for it once shown. */
    var SHOWN = 10;
    var showAllServices = Vue.ref(false);
    var shownServices = Vue.computed(function () {
      var all = store.quickAddServices.value;
      return showAllServices.value ? all : all.slice(0, SHOWN);
    });
    var hiddenCount = Vue.computed(function () {
      return Math.max(0, store.quickAddServices.value.length - SHOWN);
    });
    function toggleServices() { showAllServices.value = !showAllServices.value; }

    /* ---- the hover card on a service button ----
     *
     * All of this used to be three `title` attributes on one button — the
     * hotkey and the wait on the button itself, the closing sentence on the
     * moon, the handover plan on the arrow — which meant the desk had to know
     * which glyph to rest on to get which answer, and got a different one
     * depending on where the pointer happened to land. Worse, the handover
     * plan is the one the board most needs read: "Next: Julian" looks
     * perfectly normal, and nothing in it says Julian is only free because his
     * five o'clock is expected to end up with somebody else.
     *
     * So it is one card, the same one the *next* clock on a column header
     * opens, in the same box — a hover on this board means the same thing
     * wherever it happens. It says the whole answer at once, laid out, with
     * the handover as a list rather than a paragraph: each line is a booking
     * somebody has to go and change, and a paragraph hides how many that is.
     *
     * Fixed rather than absolute for the same reason as the other card, and
     * one at a time. It stays open while the pointer is on the card itself, so
     * a long handover list can be read down; the short delay on the way out is
     * what makes the gap between the button and the card crossable.
     */
    var peek = Vue.reactive({ serviceId: null, x: 0, y: 0, maxH: 0 });
    var peekTimer = null;
    var PEEK_W = 264;

    var peekService = Vue.computed(function () {
      if (!peek.serviceId) return null;
      return store.quickAddServices.value.find(function (s) {
        return s.id === peek.serviceId;
      }) || null;
    });

    /* A party's answer carries `seats` and `clashes` for this card; one
     * person's answer (`nextUpDetail`) has neither. Filled in empty here, so
     * the card reads one shape — without them the template throws on the
     * first hover at party size one and the whole bar is lost with it. */
    var peekDetail = Vue.computed(function () {
      var d = peek.serviceId ? nextUp.value[peek.serviceId] : null;
      return d ? Object.assign({ seats: [], clashes: [] }, d) : null;
    });

    /* The next three for the open card, Next first. Worked out only while a
     * card is open — see `nextUpList`. Empty for the two dead ends, which the
     * card already says in words. */
    var peekList = Vue.computed(function () {
      var d = peekDetail.value;
      if (!d || d.kind === 'none' || d.kind === 'wait' || d.party) return [];
      return store.nextUpList(peek.serviceId);
    });

    function openPeek(evt, svc) {
      window.clearTimeout(peekTimer);
      var r = evt.currentTarget.getBoundingClientRect();
      // Centred on the button, then pulled back inside the window: the menu
      // wraps, so a chip can sit hard against either edge of the screen.
      var half = PEEK_W / 2;
      peek.x = Math.min(Math.max(r.left + r.width / 2, half + 8),
        window.innerWidth - half - 8);
      peek.y = r.bottom + 6;
      peek.maxH = Math.max(120, window.innerHeight - peek.y - 12);
      peek.serviceId = svc.id;
    }

    function holdPeek() { window.clearTimeout(peekTimer); }

    function closePeek() {
      window.clearTimeout(peekTimer);
      peekTimer = window.setTimeout(function () { peek.serviceId = null; }, 140);
    }

    Vue.onBeforeUnmount(function () { window.clearTimeout(peekTimer); });

    // What the button is worth and how long it usually runs, on one line —
    // the two facts the weight badge and the closing check are both read off.
    // A type with no usual length says so: it is why nothing warns about it.
    function peekMeta(svc) {
      var bits = [store.weightLabel(svc.turnWeight)];
      bits.push(svc.defaultMinutes ? svc.defaultMinutes + ' min' : 'no usual length');
      return bits.join(' · ');
    }

    // The headline, in the words the Next line uses: who it would go to, and
    // when. The two dead ends say what they are instead of naming nobody.
    function peekWho(d) {
      if (d.kind === 'none') return 'Nobody in today does this';
      if (d.kind === 'wait') return 'Everyone who does it is out';
      return d.name;
    }

    // One booking the handover would have to move, as the book has it.
    function moveLine(m) {
      return store.formatClock(m.startsAt) + ' ' + m.svc.name +
        (m.appt.clientName ? ' (' + m.appt.clientName + ')' : '') +
        // Coming back in a swap: a colleague's booking, so say whose.
        (m.from ? ', ' + m.from.name + "'s" : '') +
        // Started early, alongside the rest of the visit (js/rules/same-time.js).
        (m.early ? ', booked ' + store.formatClock(m.bookedAt) + ', done alongside the visit' : '');
    }

    // A swap is a trade rather than a list of work leaving one person, and
    // the header says so.
    function movesHead(d) {
      if (d.moves.some(function (m) { return m.from; })) {
        return 'Taking this means swapping bookings so ' + d.name + ' is free:';
      }
      return 'Taking this means moving ' +
        (d.moves.length === 1 ? 'work' : d.moves.length + ' bookings') +
        ' off ' + d.name + ':';
    }

    // A party's chair, as the card lists it: who, and when they start.
    function seatWhen(x) {
      return x.wait === 'now' ? 'now' : store.formatClock(x.at);
    }

    // A handover that leans on somebody in the next chair. Matched by the
    // move itself, since the same booking cannot be in two seats' lists.
    function isClash(d, m) {
      return d.clashes.some(function (c) { return c.move.svc.id === m.svc.id &&
        c.move.startsAt === m.startsAt; });
    }

    // On a tablet a tap is a pointerenter with no matching leave, so the card
    // would be left hanging over the board after the service was armed.
    function pickService(svc) {
      peek.serviceId = null;
      store.armService(svc.id);
    }

    /* A double booking still to sort out changes who is really free, so the
     * bar says so beside its own name — one warning, the pairs in its tooltip,
     * and a press opens the conflicts list where it can be fixed. */
    var doubles = store.doubleBookings;
    var doublesLabel = Vue.computed(function () {
      var n = doubles.value.length;
      return n === 1 ? '1 double booking' : n + ' double bookings';
    });
    var doublesTitle = Vue.computed(function () {
      return 'Double booked — who is free may be wrong until this is sorted out in ' +
        store.schedulingName.value + ':\n' +
        doubles.value.map(function (d) { return d.line; }).join('\n') +
        '\nPress to see it in the appointments pane.';
    });

    return {
      doubles: doubles,
      doublesLabel: doublesLabel,
      doublesTitle: doublesTitle,
      showConflicts: store.showConflicts,
      services: shownServices,
      hiddenCount: hiddenCount,
      pendingLines: store.pendingMoveLines,
      showAllServices: showAllServices,
      toggleServices: toggleServices,
      openMissed: store.openMissedWalkin,
      missedPeople: store.missedTodayPeople,
      ui: store.ui,
      pickService: pickService,
      disarm: store.disarm,
      armedService: store.armedService,
      suggestedName: suggestedName,
      nextUp: nextUp,
      chipCols: chipCols,
      peek: peek,
      peekService: peekService,
      peekDetail: peekDetail,
      peekList: peekList,
      peekMeta: peekMeta,
      peekWho: peekWho,
      seatWhen: seatWhen,
      isClash: isClash,
      setPartySize: store.setPartySize,
      partyMin: store.partyMin,
      partyMax: store.partyMax,
      partyWindowMin: store.partyWindowMin,
      moveLine: moveLine,
      movesHead: movesHead,
      closingNote: store.closingNote,
      openPeek: openPeek,
      holdPeek: holdPeek,
      closePeek: closePeek,
      presentStaff: store.presentStaff,
      closed: store.dayClosed,
      weightBadge: store.weightBadge,
      weightLabel: store.weightLabel,
      dueSoon: store.dueSoon,
      dueSoonMinutes: store.dueSoonMinutes,
      scheduled: store.schedulingOn,
      /* Gated on the licence as well as on there being anybody on it. A salon
       * whose plan does not include the lobby screen has no tablet out and so
       * nobody waiting — but a salon whose plan LAPSED may well have rows from
       * last week still sitting there, and a list they cannot be sold the other
       * half of is a support call rather than a feature. Fails open like every
       * other licence check: unknown means shown. */
      waiting: Vue.computed(function () {
        return store.checkinLicensed.value ? store.waitingCheckins.value : [];
      })
    };
  },
  template: [
    '<div class="service-bar">',
    /* Only the two tools. The bar is named over the service menu itself, where
       the label sits level with Due now's and each column says what it is. */
    '  <div class="service-bar__head">',
    '    <div class="service-bar__tools">',
    '    <button type="button" class="miss-btn"',
    '      title="Somebody came in and left without being seen. Logs how many and what they wanted."',
    '      @click="openMissed">',
    '      Missed walk-in',
    '      <span v-if="missedPeople" class="miss-btn__n">{{ missedPeople }}</span>',
    '    </button>',
    /* No Request button here any more (Kevin, 2026-10-19): who a client asked
       for comes from Mangomint, read every minute — see "client requests on a
       booking" in the store. */
    '    </div>',
    '  </div>',
    /* The two paths sit side by side rather than stacked: they answer different
       questions — what a walk-in wants, and who is about to walk in — and
       neither is a step towards the other. Stacked, a quiet afternoon still
       pushed the board an inch down the screen for one due chip. With nothing
       due there is one column and the bar reads exactly as it did.

       The menu is first in the source as well as on the screen, which is what
       keeps it on the left edge when Due now empties out mid-afternoon and the
       grid drops to one column.

       Both lists go when the book is shut. Neither is a thing to read — one is
       a menu of services to start and the other a row of clients to seat, and
       with nothing able to go on the board every row in both is a dead end.
       Leaving them up would be offering the desk a choice that is already
       made; the notice above the grid says so, and the book itself is still
       there in full in the appointments pane. */
    /* `has-side` rather than `has-due`: what the class governs is whether the
       menu shares the bar with anything at all, and there are two things it can
       now share it with. The grid flows into however many columns are actually
       rendered — see .service-bar__cols — so nothing here has to enumerate the
       four combinations of "a book" and "somebody waiting". */
    '  <div v-if="!closed" class="service-bar__cols"',
    '    :class="{ \'has-side\': (scheduled && dueSoon.length) || waiting.length || pendingLines.length }">',
    /* ---- services: the walk-in path ---- */
    '  <div class="service-bar__col">',
    /* Unconditional: it is the bar's own name now, not a divider between two
       rows, so it is there whether or not anything is due. */
    '  <span class="service-bar__sublabel">Next availability',
    '    <button v-if="scheduled && doubles.length" type="button" class="nextavail-warn"',
    '      :title="doublesTitle" :aria-label="doublesTitle" @click="showConflicts">',
    '      &#9888; {{ doublesLabel }}</button>',
    /* How many people the rows answer for. One is the board as it always was;
       more asks every row for that many chairs of the same service, starting
       within a few minutes of each other. */
    '    <span class="party-size" role="group" aria-label="Party size"',
    '      :class="{ \'is-on\': ui.partySize > 1 }"',
    '      :title="\'Seat this many people together for the same service, starting within \' + partyWindowMin + \' minutes of each other\'">',
    '      <span class="party-size__label">Party size</span>',
    '      <button type="button" class="party-size__btn" aria-label="One fewer"',
    '        :disabled="ui.partySize <= partyMin" @click="setPartySize(ui.partySize - 1)">&minus;</button>',
    '      <input type="number" class="party-size__input" inputmode="numeric"',
    '        :min="partyMin" :max="partyMax" :value="ui.partySize" aria-label="Party size"',
    '        @change="setPartySize($event.target.value); $event.target.value = ui.partySize">',
    '      <button type="button" class="party-size__btn" aria-label="One more"',
    '        :disabled="ui.partySize >= partyMax" @click="setPartySize(ui.partySize + 1)">+</button>',
    '    </span>',
    '  </span>',
    /* The list is the whole of this column now — the flex row that used to
       hold it beside other things would size a grid to its content and give
       the list one column however wide the bar got. */
    '    <div class="service-chips" :style="{ \'--cols\': chipCols }">',
    '      <button v-for="svc in services" :key="svc.id" type="button"',
    '        class="chip chip--svc"',
    /* Greyed for the two dead ends: nobody does it, and the day is not long
       enough for it. A service everybody who does it is merely mid-service on
       still gets booked today — it says when, below. */
    '        :class="{ \'chip--on\': ui.armedServiceId === svc.id,',
    '                  \'chip--none\': ui.showNextUp && nextUp[svc.id].kind === \'none\',',
    '                  \'chip--late\': nextUp[svc.id].closing,',
    '                  \'chip--shut\': nextUp[svc.id].closing && nextUp[svc.id].closing.blocked }"',
    '        :style="{ \'--chip\': svc.color }"',
    /* Only where there is one. `aria-keyshortcuts=""` is a promise of a
       keystroke that does nothing, read out to somebody who cannot see the
       button to find out otherwise. */
    '        :aria-keyshortcuts="svc.shortcut || null"',
    '        @pointerenter="openPeek($event, svc)" @pointerleave="closePeek"',
    '        @focus="openPeek($event, svc)" @blur="closePeek"',
    '        @click="pickService(svc)">',
    /* One line, in reading order: what it is, then who it goes to. The two
       used to be stacked inside a card, which cost the bar a second row of
       type per service and made ten services a block the board had to be
       pushed down for. Flat rather than in a `chip__top` wrapper — with the
       card gone there is no second row for the top of one to be. */
    '        <span class="chip__letter">{{ svc.abbrev }}</span>',
    '        <span class="chip__name">{{ svc.name }}</span>',
    /* The technician on the Next line is only free because the book is
       expected to change hands. Nothing is wrong with the row — it is an
       instruction attached to taking it, and the hover card is the
       instruction. */
    '        <span v-if="nextUp[svc.id].moves.length" class="chip__moves"',
    '          aria-label="Taking this means moving work off that technician">&#8644;</span>',
    /* Past closing. Not a refusal on its own — inside the grace it is a
       question for whoever it lands on, and the hover card is how to ask it.
       The row greys out as well only once it is too far over to be offered. */
    '        <span v-if="nextUp[svc.id].closing" class="chip__late"',
    '          aria-label="Would run past closing">&#9790;</span>',
    '        <span v-if="svc.turnWeight !== 1" class="chip__half"',
    '          :class="{ \'is-zero\': svc.turnWeight === 0 }"',
    '          :aria-label="weightLabel(svc.turnWeight)">{{ weightBadge(svc.turnWeight) }}</span>',
    /* The name reads plainly when it can be taken now and quietly when it is a
       wait; the badge beside it is how long that wait is. Nobody at all, and
       nobody with an end time to give, both read as the flat word they are.
       Held to a fixed width at the end of the row so the names line up down
       one column and the waits down another, which is what makes a list of
       these readable at a glance rather than ten separate sentences. */
    '        <span v-if="ui.showNextUp" class="chip__next">',
    '          <span class="chip__next-label">Next</span>',
    '          <span class="chip__next-name"',
    '            :class="{ \'is-none\': nextUp[svc.id].kind === \'none\' || nextUp[svc.id].kind === \'wait\',',
    '                      \'is-wait\': nextUp[svc.id].kind === \'eta\' }">',
    '            {{ nextUp[svc.id].name }}</span>',
    '          <span v-if="nextUp[svc.id].wait" class="chip__next-eta">{{ nextUp[svc.id].wait }}</span>',
    '        </span>',
    '      </button>',
    '    </div>',
    '    <button v-if="hiddenCount" type="button" class="link-btn service-chips__more"',
    '      :aria-expanded="showAllServices" @click="toggleServices">',
    '      {{ showAllServices ? "Fewer" : hiddenCount + " more" }}</button>',
    '  </div>',
    '',
    /* ---- due now: bookings about to start ----
       The chips themselves are in due-now.js: a phone reads the same list out
       of a collapsed strip, and one question deserves one answer. */
    '  <due-now v-if="scheduled && dueSoon.length" />',
    /* Beside it: what the book still has to be told. Draws nothing when there is
       nothing, and takes its own column in the grid when there is. */
    '  <pending-moves />',
    '',
    /* ---- waiting: who is already in the room ----
       Unlike the two beside it this needs no plugin and no setting — it is
       there exactly when somebody is on it. */
    '  <waiting-list v-if="waiting.length" />',
    '  </div>',
    '  <div v-if="armedService" class="service-bar__hint is-armed">',
    '    <span v-if="ui.requestMode"><strong>{{ armedService.name }}</strong> request &mdash; tap the requested tech\'s column.</span>',
    '    <span v-else-if="suggestedName"><strong>{{ armedService.name }}</strong> armed &mdash; next up is <strong>{{ suggestedName }}</strong>. Tap any column to assign.</span>',
    '    <span v-else><strong>{{ armedService.name }}</strong> armed &mdash; tap a column to assign.</span>',
    '    <button type="button" class="link-btn" @click="disarm">Cancel</button>',
    '  </div>',
    /* Not on a closed day: nobody being on the board is only worth saying
       while somebody could still be put on one. */
    '  <p v-else-if="!presentStaff.length && !closed" class="service-bar__hint">Nobody is on the board yet.</p>',
    /* Outside the bar and fixed to the window, the same as the board's other
       card: the menu wraps and scrolls with the page, and anything positioned
       inside it would be clipped or carried off. */
    '  <div v-if="peekDetail && peekService" class="peek-card svc-peek"',
    '    :style="{ left: peek.x + \'px\', top: peek.y + \'px\', maxHeight: peek.maxH + \'px\' }"',
    '    @pointerenter="holdPeek" @pointerleave="closePeek">',
    /* The keyboard's way in, for the services that have one. A service with no
       key gets no line rather than an empty "Press" — the button being hovered
       is already the other way in, and saying "click it" to somebody with the
       pointer on it is noise. */
    '    <div v-if="peekService.shortcut" class="peek-card__head">Press {{ peekService.shortcut }}</div>',
    '    <div class="svc-peek__svc">',
    '      <span class="svc-peek__letter" :style="{ \'--chip\': peekService.color }">',
    '        {{ peekService.abbrev }}</span>',
    '      <span class="svc-peek__name">{{ peekService.name }}</span>',
    '    </div>',
    '    <div class="svc-peek__meta">{{ peekMeta(peekService) }}</div>',
    /* The answer the button is read for, in the same words as the Next line
       under it — so the card confirms what the button said rather than
       phrasing it a second way. */
    '    <div v-if="!peekList.length && !peekDetail.seats.length" class="svc-peek__next">',
    '      <span class="svc-peek__next-label">Next</span>',
    '      <span class="svc-peek__next-name"',
    '        :class="{ \'is-none\': peekDetail.kind === \'none\' || peekDetail.kind === \'wait\' }">',
    '        {{ peekWho(peekDetail) }}</span>',
    '      <span v-if="peekDetail.wait" class="svc-peek__next-eta">{{ peekDetail.wait }}</span>',
    '    </div>',
    /* Next, then the two after it by when they could start. The first row is
       the button's own Next line; the others are who the desk turns to when
       that one will not do. */
    '    <ol v-else class="svc-peek__list">',
    '      <li v-for="(e, i) in peekList" :key="e.staff.id" class="svc-peek__next"',
    '        :class="{ \'is-then\': i > 0 }">',
    '        <span class="svc-peek__next-label">{{ i === 0 ? "Next" : "Then" }}</span>',
    '        <span class="svc-peek__next-name" :class="{ \'is-wait\': e.wait !== \'now\' }">',
    '          {{ e.staff.name }}</span>',
    '        <span v-if="e.moves" class="svc-peek__moves-mark"',
    '          title="Only free if work moves off them">&#8644;</span>',
    '        <span class="svc-peek__next-eta">{{ e.wait }}</span>',
    '      </li>',
    '    </ol>',
    /* A party: one line per chair, and under each the bookings that have to
       change hands for that technician to be free. A handover onto somebody
       who is in another chair of the same party is marked — that plan was
       made for one walk-in and does not hold for two. */
    '    <ol v-if="peekDetail.seats.length" class="svc-peek__list svc-peek__party">',
    '      <li class="svc-peek__party-head">Party of {{ peekDetail.party }}, within {{ partyWindowMin }} min</li>',
    '      <li v-for="(x, i) in peekDetail.seats" :key="x.id">',
    '        <div class="svc-peek__next">',
    '          <span class="svc-peek__next-label">Chair {{ i + 1 }}</span>',
    '          <span class="svc-peek__next-name" :class="{ \'is-wait\': x.wait !== \'now\' }">',
    '            {{ x.staff ? x.staff.name : "?" }}</span>',
    '          <span v-if="x.moves.length" class="svc-peek__moves-mark"',
    '            title="Only free if work moves off them">&#8644;</span>',
    '          <span class="svc-peek__next-eta">{{ seatWhen(x) }}</span>',
    '        </div>',
    '        <ul v-if="x.moves.length" class="svc-peek__moves">',
    '          <li v-for="m in x.moves" :key="m.svc.id" :class="{ \'is-clash\': isClash(peekDetail, m) }">',
    '            <span class="svc-peek__move-what">{{ moveLine(m) }}</span>',
    '            <span class="svc-peek__move-to">&rarr; {{ m.to.name }}<note-warn :client-id="m.appt.clientId" :staff-id="m.to.id" /></span>',
    '            <span v-if="isClash(peekDetail, m)" class="svc-peek__clash">{{ m.to.name }} is in another chair of this party &mdash; this one needs somebody else.</span>',
    '          </li>',
    '        </ul>',
    '      </li>',
    '    </ol>',
    '    <p v-if="peekDetail.party && peekDetail.moves.length" class="peek-card__note svc-peek__moves-tail">',
    '      Nothing here moves {{ peekDetail.moves.length === 1 ? "it" : "them" }} &mdash; that is a change',
    '      to make in the booking system.</p>',
    /* Where the wait comes from. Nothing to say when somebody is simply free,
       and the card stops at the line above. */
    '    <div v-if="peekDetail.why" class="peek-card__note">{{ peekDetail.why }}</div>',
    /* Past closing: still offered, still a question for whoever it lands on,
       and once it is too far over the button is greyed and this says why. */
    '    <div v-if="peekDetail.closing" class="peek-card__note svc-peek__late"',
    '      :class="{ \'is-blocked\': peekDetail.closing.blocked }">',
    '      <span class="svc-peek__moon" aria-hidden="true">&#9790;</span>',
    '      {{ closingNote(peekService.id, peekDetail.closing) }}',
    '    </div>',
    /* The handover, as a list. Each line is a booking somebody has to go and
       change in the booking system, and a paragraph of them hides how many
       that is. */
    '    <div v-if="peekDetail.moves.length && !peekDetail.party" class="peek-card__note">',
    '      <span class="svc-peek__moves-mark" aria-hidden="true">&#8644;</span>',
    '      {{ movesHead(peekDetail) }}',
    '      <ul class="svc-peek__moves">',
    '        <li v-for="m in peekDetail.moves" :key="m.svc.id">',
    '          <span class="svc-peek__move-what">{{ moveLine(m) }}</span>',
    '          <span class="svc-peek__move-to">&rarr; {{ m.to.name }}<note-warn :client-id="m.appt.clientId" :staff-id="m.to.id" /></span>',
    '        </li>',
    '      </ul>',
    '      <p class="svc-peek__moves-tail">Nothing here moves',
    '        {{ peekDetail.moves.length === 1 ? "it" : "them" }} &mdash; that is a change',
    '        to make in the booking system.</p>',
    '    </div>',
    '  </div>',
    '</div>'
  ].join('\n')
};
